import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { CanComponentDeactivate } from 'src/app/guards/unsaved-changes.guard';
import {
  faPlus, faTrash, faTimes, faGripVertical, faEdit, faCheck,
  faExclamationTriangle, faSave, faArrowLeft, faChevronDown, faChevronUp
} from '@fortawesome/free-solid-svg-icons';
import { AuthService } from 'src/app/services/auth.service';
import { IncidentService } from 'src/app/services/incident.service';
import { IIncidentSection, IIncidentQuestion, IIncidentQuestionOption, IIncidentObjectField } from 'src/app/models/incident.model';
import { setWarningText } from 'src/app/reducers/warning-popup.reducer';
import { IError } from 'src/app/models/error.model';

@Component({
  selector: 'app-incident-questions',
  templateUrl: './incident-questions.component.html'
})
export class IncidentQuestionsComponent implements OnInit, CanComponentDeactivate {

  // ─── Ícones ────────────────────────────────────────────────────────
  faPlus = faPlus;
  faTrash = faTrash;
  faTimes = faTimes;
  faGripVertical = faGripVertical;
  faEdit = faEdit;
  faCheck = faCheck;
  faExclamationTriangle = faExclamationTriangle;
  faSave = faSave;
  faArrowLeft = faArrowLeft;
  faChevronDown = faChevronDown;
  faChevronUp = faChevronUp;

  // ─── Estado ────────────────────────────────────────────────────────
  isRoot = false;
  loading = false;
  publishLoading = false;
  currentVersion = 0;

  // Seções fixas do formulário (fallback caso a API não retorne)
  fixedSections: { id: string; title: string; order: number }[] = [
    { id: 'section_1',  title: 'Dados do Procedimento Original', order: 1 },
    { id: 'section_2',  title: 'Cronologia da Intercorrência', order: 2 },
    { id: 'section_3',  title: 'Tipo de Intercorrência', order: 3 },
    { id: 'section_4',  title: 'Descrição Detalhada da Intercorrência', order: 4 },
    { id: 'section_5',  title: 'Classificação de Gravidade da Intercorrência', order: 5 },
    { id: 'section_6',  title: 'Tratamento Instituído para Reverter a Intercorrência', order: 6 },
    { id: 'section_7',  title: 'Resposta ao Tratamento', order: 7 },
    { id: 'section_8',  title: 'Exames Complementares Realizados', order: 8 },
    { id: 'section_9',  title: 'Encaminhamentos Realizados', order: 9 },
    { id: 'section_10', title: 'Consequências da Intercorrência', order: 10 },
    { id: 'section_11', title: 'Comunicação com o Paciente', order: 11 },
    { id: 'section_12', title: 'Plano de Seguimento', order: 12 },
    { id: 'section_13', title: 'Análise do Evento', order: 13 }
  ];

  // Seções com suas perguntas (estado local)
  sections: IIncidentSection[] = [];

  // Tipos de pergunta disponíveis
  questionTypes: { value: string; label: string }[] = [
    { value: 'TEXT_INPUT', label: 'Texto Livre' },
    { value: 'YES_NO', label: 'Sim / Não' },
    { value: 'SINGLE_CHOICE', label: 'Escolha Única' },
    { value: 'MULTIPLE_CHOICE', label: 'Múltipla Escolha' },
    { value: 'DATE', label: 'Data' },
    { value: 'TIME', label: 'Hora' },
    { value: 'OBJECT', label: 'Objeto (ex: Medicamento)' }
  ];

  // Tipos de campo disponíveis para OBJECT
  objectFieldTypes: { value: string; label: string }[] = [
    { value: 'TEXT', label: 'Texto' },
    { value: 'NUMBER', label: 'Número' },
    { value: 'TIME', label: 'Hora' },
    { value: 'DATE', label: 'Data' },
    { value: 'SELECT', label: 'Seleção' }
  ];

  // Controle de seções expandidas
  expandedSections: Record<string, boolean> = {};

  // Pergunta em edição
  editingQuestionId: string | null = null;
  editForm!: FormGroup;

  // Modais
  showPublishConfirmation = false;
  showDeleteConfirmation = false;
  questionToDelete: { sectionIndex: number; questionIndex: number } | null = null;
  showUnsavedChangesModal = false;

  // Validação
  showValidationErrors = false;
  validationErrorMessages: string[] = [];

  // Controle de alterações não publicadas
  isDirty = false;
  private deactivateSubject: Subject<boolean> | null = null;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private authService: AuthService,
    private incidentService: IncidentService,
    private store: Store<{ warningPopup: IError }>,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.isRoot = this.authService.getPermission() === 'ROOT';

    if (!this.isRoot) {
      this.router.navigate(['/main/v2']);
      return;
    }

    // Expandir todas as seções por padrão
    this.fixedSections.forEach(s => this.expandedSections[s.id] = true);

    this.loadQuestions();
  }

  // ─── Carregamento ──────────────────────────────────────────────────

  private initEmptySections(): void {
    this.sections = this.fixedSections.map(s => ({
      id: s.id,
      section_title: s.title,
      section_order: s.order,
      questions: []
    }));
  }

  async loadQuestions(): Promise<void> {
    this.loading = true;

    try {
      // ── Passo 1: buscar seções master (GET /incidents/sections)
      // A API retorna um ARRAY direto: [{id, label}, {id, label}, ...]
      const sectionsRaw: any = await this.incidentService.getGlobalQuestions();
      console.log('Configuração de perguntas carregada:', sectionsRaw);

      // Normalizar: aceitar tanto array direto quanto {sections: [...]}
      const sectionsArray: any[] = Array.isArray(sectionsRaw)
        ? sectionsRaw
        : (sectionsRaw.sections || []);

      // Converter para o formato interno IIncidentSection (sem perguntas ainda)
      this.sections = sectionsArray.map((s: any, idx: number) => ({
        id: s.id || s.section_id || '',
        section_title: s.label || s.section_name || s.section_title || s.title || '',
        section_order: s.section_order || s.order || (idx + 1),
        questions: []
      } as IIncidentSection));

      // Expandir todas as seções por padrão
      this.sections.forEach(s => this.expandedSections[s.id] = true);

      // ── Passo 2: buscar perguntas publicadas (GET /incidents/questions/latest)
      try {
        const latest: any = await this.incidentService.getLatestQuestions();
        console.log('Latest questions response:', latest);

        this.currentVersion = latest?.version || 0;

        const latestSections: any[] = Array.isArray(latest?.sections) ? latest.sections : [];

        // Mesclar perguntas nas seções por section_id
        for (const ls of latestSections) {
          const sectionId = ls.section_id || ls.id || '';
          const target = this.sections.find(s => s.id === sectionId);

          if (target) {
            // Normalizar perguntas
            target.questions = (Array.isArray(ls.questions) ? ls.questions : []).map((q: any) => ({
              id: q.id || '',
              question_text: q.label ?? q.question_text ?? '',
              type: q.type || 'TEXT_INPUT',
              is_required: q.is_required ?? false,
              placeholder: q.placeholder ?? '',
              order_in_section: q.question_order ?? q.order_in_section ?? 0,
              options: (Array.isArray(q.options) ? q.options : []).map((o: any) => ({
                id: o.id || '',
                option_text: o.option_label ?? o.option_text ?? '',
                option_order: o.option_order ?? 0,
                triggers_free_text: o.triggers_free_text ?? false,
                sub_questions: (Array.isArray(o.sub_questions) ? o.sub_questions : []).map((sq: any) => ({
                  id: sq.id || '',
                  question_text: sq.label ?? sq.question_text ?? '',
                  type: sq.type || 'TEXT_INPUT',
                  is_required: sq.is_required ?? false,
                  placeholder: sq.placeholder ?? '',
                  order_in_section: sq.question_order ?? sq.order_in_section ?? 0,
                  options: (Array.isArray(sq.options) ? sq.options : []).map((sqo: any) => ({
                    id: sqo.id || '',
                    option_text: sqo.option_label ?? sqo.option_text ?? '',
                    option_order: sqo.option_order ?? 0,
                    triggers_free_text: sqo.triggers_free_text ?? false
                  }))
                } as IIncidentQuestion))
              })),
              object_fields: (Array.isArray(q.object_fields) ? q.object_fields : []).map((f: any) => ({
                id: f.id || '',
                field_key: f.field_key || '',
                field_label: f.field_label || '',
                field_type: f.field_type || 'TEXT',
                placeholder: f.placeholder || '',
                is_required: f.is_required ?? false,
                field_order: f.field_order ?? 0,
                select_options: Array.isArray(f.select_options) ? f.select_options : []
              }))
            } as IIncidentQuestion));
          }
        }
      } catch (innerErr: any) {
        // 404 = sem versão publicada ainda — não é erro
        if (innerErr?.status !== 404) {
          console.warn('Erro ao buscar perguntas publicadas:', innerErr);
        }
      }

      // Se não há seções (API retornou vazio), fallback para seções fixas
      if (this.sections.length === 0) {
        this.initEmptySections();
      }

      // Ordenar seções e perguntas
      this.sections.sort((a, b) => a.section_order - b.section_order);
      this.sections.forEach(s => {
        s.questions.sort((a, b) => a.order_in_section - b.order_in_section);
      });

      this.isDirty = false;
    } catch (error: any) {
      // Qualquer erro: inicializar estrutura vazia para permitir cadastro
      this.initEmptySections();
      // Só mostra erro se não for 404 (clínica sem perguntas é esperado)
      if (error?.status && error.status !== 404) {
        console.error('Erro ao carregar perguntas de intercorrência:', error);
        this.store.dispatch(setWarningText('Erro ao carregar perguntas. Tente novamente.', 500));
      }
    } finally {
      this.loading = false;
    }
  }

  // ─── Gerenciamento de Perguntas ────────────────────────────────────

  toggleSection(sectionId: string): void {
    this.expandedSections[sectionId] = !this.expandedSections[sectionId];
  }

  addQuestion(sectionIndex: number): void {
    const section = this.sections[sectionIndex];
    const newOrder = section.questions.length + 1;
    const tempId = 'temp_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);

    const newQuestion: IIncidentQuestion = {
      id: tempId,
      question_text: '',
      type: 'TEXT_INPUT',
      is_required: false,
      placeholder: '',
      order_in_section: newOrder,
      options: []
    };

    section.questions.push(newQuestion);
    this.startEditing(newQuestion);
  }

  startEditing(question: IIncidentQuestion): void {
    this.editingQuestionId = question.id;
    this.editForm = this.fb.group({
      question_text: [question.question_text, Validators.required],
      type: [question.type, Validators.required],
      is_required: [question.is_required],
      placeholder: [question.placeholder || ''],
      options: this.fb.array(
        (question.options || []).map(opt => this.createOptionGroup(opt))
      ),
      object_fields: this.fb.array(
        (question.object_fields || []).map(field => this.createObjectFieldGroup(field))
      )
    });
  }

  private createOptionGroup(option?: IIncidentQuestionOption): FormGroup {
    return this.fb.group({
      id: [option?.id || 'opt_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)],
      option_text: [option?.option_text || '', Validators.required],
      option_order: [option?.option_order || 1],
      triggers_free_text: [option?.triggers_free_text || false],
      sub_questions: this.fb.array(
        (option?.sub_questions || []).map(sq => this.createSubQuestionGroup(sq))
      )
    });
  }

  /**
   * Cria um FormGroup para uma sub-pergunta dentro de uma opção.
   */
  private createSubQuestionGroup(subQ?: IIncidentQuestion): FormGroup {
    return this.fb.group({
      id: [subQ?.id || 'subq_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)],
      question_text: [subQ?.question_text || '', Validators.required],
      type: [subQ?.type || 'TEXT_INPUT', Validators.required],
      is_required: [subQ?.is_required || false],
      placeholder: [subQ?.placeholder || ''],
      options: this.fb.array(
        (subQ?.options || []).map(opt => this.fb.group({
          id: [opt.id || 'opt_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)],
          option_text: [opt.option_text || '', Validators.required],
          option_order: [opt.option_order || 1],
          triggers_free_text: [opt.triggers_free_text || false]
        }))
      )
    });
  }

  get editOptionsArray(): FormArray {
    return this.editForm?.get('options') as FormArray;
  }

  addOption(): void {
    const arr = this.editOptionsArray;
    arr.push(this.createOptionGroup({
      id: 'opt_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9),
      option_text: '',
      option_order: arr.length + 1,
      triggers_free_text: false
    }));
  }

  removeOption(index: number): void {
    this.editOptionsArray.removeAt(index);
    // Reordenar
    this.editOptionsArray.controls.forEach((ctrl, i) => {
      ctrl.get('option_order')?.setValue(i + 1);
    });
  }

  // ─── Sub-Perguntas ─────────────────────────────────────────────────

  getSubQuestionsArray(optionIndex: number): FormArray {
    return this.editOptionsArray.at(optionIndex).get('sub_questions') as FormArray;
  }

  getSubQuestionOptionsArray(optionIndex: number, subQIndex: number): FormArray {
    return this.getSubQuestionsArray(optionIndex).at(subQIndex).get('options') as FormArray;
  }

  addSubQuestion(optionIndex: number): void {
    const arr = this.getSubQuestionsArray(optionIndex);
    arr.push(this.createSubQuestionGroup());
  }

  removeSubQuestion(optionIndex: number, subQIndex: number): void {
    this.getSubQuestionsArray(optionIndex).removeAt(subQIndex);
  }

  addSubQuestionOption(optionIndex: number, subQIndex: number): void {
    const arr = this.getSubQuestionOptionsArray(optionIndex, subQIndex);
    arr.push(this.fb.group({
      id: ['opt_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)],
      option_text: ['', Validators.required],
      option_order: [arr.length + 1],
      triggers_free_text: [false]
    }));
  }

  removeSubQuestionOption(optionIndex: number, subQIndex: number, optIdx: number): void {
    const arr = this.getSubQuestionOptionsArray(optionIndex, subQIndex);
    arr.removeAt(optIdx);
    arr.controls.forEach((ctrl, i) => {
      ctrl.get('option_order')?.setValue(i + 1);
    });
  }

  // ─── Campos de Objeto (tipo OBJECT) ────────────────────────────────

  /**
   * Cria um FormGroup para um campo de objeto.
   */
  private createObjectFieldGroup(field?: IIncidentObjectField): FormGroup {
    return this.fb.group({
      id: [field?.id || 'field_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)],
      field_key: [field?.field_key || crypto.randomUUID()],
      field_label: [field?.field_label || '', Validators.required],
      field_type: [field?.field_type || 'TEXT', Validators.required],
      placeholder: [field?.placeholder || ''],
      is_required: [field?.is_required || false],
      field_order: [field?.field_order || 1],
      select_options_text: [(field?.select_options || []).join(', ')]
    });
  }

  get editObjectFieldsArray(): FormArray {
    return this.editForm?.get('object_fields') as FormArray;
  }

  addObjectField(): void {
    const arr = this.editObjectFieldsArray;
    arr.push(this.createObjectFieldGroup({
      field_key: crypto.randomUUID(),
      field_label: '',
      field_type: 'TEXT',
      is_required: false,
      field_order: arr.length + 1
    }));
  }

  removeObjectField(index: number): void {
    this.editObjectFieldsArray.removeAt(index);
    this.editObjectFieldsArray.controls.forEach((ctrl, i) => {
      ctrl.get('field_order')?.setValue(i + 1);
    });
  }

  /**
   * Verifica se o tipo de pergunta é OBJECT.
   */
  isObjectType(type: string): boolean {
    return type === 'OBJECT';
  }

  /**
   * Retorna o label de um tipo de campo de objeto.
   */
  getFieldTypeLabel(fieldType: string): string {
    return this.objectFieldTypes.find(t => t.value === fieldType)?.label || fieldType;
  }

  saveQuestion(): void {
    if (!this.editForm.valid) {
      this.editForm.markAllAsTouched();
      return;
    }

    const formValue = this.editForm.value;
    const questionId = this.editingQuestionId!;

    // Encontrar a pergunta nas seções
    for (const section of this.sections) {
      const qIndex = section.questions.findIndex(q => q.id === questionId);
      if (qIndex !== -1) {
        section.questions[qIndex] = {
          ...section.questions[qIndex],
          question_text: formValue.question_text,
          type: formValue.type,
          is_required: formValue.is_required,
          placeholder: formValue.placeholder,
          options: this.needsOptions(formValue.type)
            ? formValue.options.map((o: any) => ({
                ...o,
                sub_questions: (o.sub_questions || []).map((sq: any) => ({
                  ...sq,
                  options: this.needsOptions(sq.type) ? sq.options : []
                }))
              }))
            : [],
          object_fields: this.isObjectType(formValue.type)
            ? formValue.object_fields.map((f: any, idx: number) => ({
                id: f.id,
                field_key: f.field_key,
                field_label: f.field_label,
                field_type: f.field_type,
                placeholder: f.placeholder || '',
                is_required: f.is_required || false,
                field_order: idx + 1,
                select_options: f.field_type === 'SELECT' && f.select_options_text
                  ? f.select_options_text.split(',').map((s: string) => s.trim()).filter((s: string) => s)
                  : []
              }))
            : []
        };
        break;
      }
    }

    this.isDirty = true;
    this.editingQuestionId = null;
  }

  cancelEditing(): void {
    // Se é uma pergunta nova sem texto, remover
    if (this.editingQuestionId) {
      for (const section of this.sections) {
        const qIndex = section.questions.findIndex(q => q.id === this.editingQuestionId);
        if (qIndex !== -1 && !section.questions[qIndex].question_text) {
          section.questions.splice(qIndex, 1);
          break;
        }
      }
    }
    this.editingQuestionId = null;
  }

  confirmDeleteQuestion(sectionIndex: number, questionIndex: number): void {
    this.questionToDelete = { sectionIndex, questionIndex };
    this.showDeleteConfirmation = true;
  }

  deleteQuestion(): void {
    if (!this.questionToDelete) return;
    const { sectionIndex, questionIndex } = this.questionToDelete;

    // Remover apenas localmente — a exclusão efetiva acontece ao publicar
    // (nova versão é criada sem esta pergunta)
    this.sections[sectionIndex].questions.splice(questionIndex, 1);
    // Reordenar
    this.sections[sectionIndex].questions.forEach((q, i) => {
      q.order_in_section = i + 1;
    });

    this.isDirty = true;
    this.store.dispatch(setWarningText('Pergunta removida. Publique para aplicar as alterações.', 200));

    this.showDeleteConfirmation = false;
    this.questionToDelete = null;
  }

  moveQuestion(sectionIndex: number, questionIndex: number, direction: 'up' | 'down'): void {
    const questions = this.sections[sectionIndex].questions;
    const targetIndex = direction === 'up' ? questionIndex - 1 : questionIndex + 1;
    if (targetIndex < 0 || targetIndex >= questions.length) return;

    [questions[questionIndex], questions[targetIndex]] = [questions[targetIndex], questions[questionIndex]];

    questions.forEach((q, i) => q.order_in_section = i + 1);
    this.isDirty = true;
  }

  // ─── Helpers ───────────────────────────────────────────────────────

  needsOptions(type: string): boolean {
    return type === 'SINGLE_CHOICE' || type === 'MULTIPLE_CHOICE';
  }

  getTypeLabel(type: string): string {
    return this.questionTypes.find(t => t.value === type)?.label || type;
  }

  getTotalQuestions(): number {
    return this.sections.reduce((sum, s) => sum + s.questions.length, 0);
  }

  // ─── Publicação ────────────────────────────────────────────────────

  requestPublish(): void {
    // Validar antes de publicar
    const errors = this.validateAllQuestions();
    if (errors.length > 0) {
      this.validationErrorMessages = errors;
      this.showValidationErrors = true;
      return;
    }
    this.showPublishConfirmation = true;
  }

  private validateAllQuestions(): string[] {
    const errors: string[] = [];

    for (const section of this.sections) {
      for (const question of section.questions) {
        if (!question.question_text.trim()) {
          errors.push(`Seção "${section.section_title}": há uma pergunta que não foi salva.`);
        }
        if (this.needsOptions(question.type) && (!question.options || question.options.length < 2)) {
          errors.push(`Pergunta "${question.question_text || '(sem texto)'}": precisa de ao menos 2 opções.`);
        }
        if (this.needsOptions(question.type)) {
          for (const opt of question.options) {
            if (!opt.option_text.trim()) {
              errors.push(`Pergunta "${question.question_text}": há uma opção sem texto.`);
            }
            // Validar sub-perguntas da opção
            if (opt.sub_questions && opt.sub_questions.length > 0) {
              for (const subQ of opt.sub_questions) {
                if (!subQ.question_text.trim()) {
                  errors.push(`Opção "${opt.option_text}" da pergunta "${question.question_text}": sub-pergunta sem texto.`);
                }
                if (this.needsOptions(subQ.type) && (!subQ.options || subQ.options.length < 2)) {
                  errors.push(`Sub-pergunta "${subQ.question_text || '(sem texto)'}": precisa de ao menos 2 opções.`);
                }
                if (this.needsOptions(subQ.type)) {
                  for (const subOpt of subQ.options) {
                    if (!subOpt.option_text.trim()) {
                      errors.push(`Sub-pergunta "${subQ.question_text}": há uma opção sem texto.`);
                    }
                  }
                }
              }
            }
          }
        }
        // Validação para tipo OBJECT
        if (this.isObjectType(question.type)) {
          if (!question.object_fields || question.object_fields.length === 0) {
            errors.push(`Pergunta "${question.question_text || '(sem texto)'}": tipo Objeto precisa de ao menos 1 campo.`);
          } else {
            for (const field of question.object_fields) {
              if (!field.field_label.trim()) {
                errors.push(`Pergunta "${question.question_text}": há um campo de objeto sem label.`);
              }
              if (field.field_type === 'SELECT' && (!field.select_options || field.select_options.length === 0)) {
                errors.push(`Campo "${field.field_label}" da pergunta "${question.question_text}": tipo Seleção precisa de opções.`);
              }
            }
          }
        }
      }
    }

    return errors;
  }

  async publish(): Promise<void> {
    this.showPublishConfirmation = false;
    this.publishLoading = true;

    try {
      const payload = {
        sections: this.sections.map(s => ({
          id: s.id,
          section_title: s.section_title,
          questions: s.questions.map((q, idx) => ({
            // Omitir IDs temporários para que o backend gere novos
            ...(q.id && !q.id.startsWith('temp_') ? { id: q.id } : {}),
            question_text: q.question_text,
            type: q.type,
            is_required: q.is_required,
            placeholder: q.placeholder || '',
            order: idx + 1,
            options: (q.options || []).map((o, optIdx) => ({
              ...(o.id && !o.id.startsWith('opt_') ? { id: o.id } : {}),
              option_text: o.option_text,
              option_order: optIdx + 1,
              triggers_free_text: o.triggers_free_text || false,
              sub_questions: (o.sub_questions || []).map((sq, sqIdx) => ({
                ...(sq.id && !sq.id.startsWith('subq_') && !sq.id.startsWith('temp_') ? { id: sq.id } : {}),
                label: sq.question_text,
                type: sq.type,
                is_required: sq.is_required || false,
                placeholder: sq.placeholder || '',
                question_order: sqIdx + 1,
                options: (sq.options || []).map((sqOpt, sqOptIdx) => ({
                  ...(sqOpt.id && !sqOpt.id.startsWith('opt_') ? { id: sqOpt.id } : {}),
                  option_text: sqOpt.option_text,
                  option_order: sqOptIdx + 1,
                  triggers_free_text: sqOpt.triggers_free_text || false
                }))
              }))
            })),
            // Campos de objeto (tipo OBJECT)
            ...(q.type === 'OBJECT' && q.object_fields && q.object_fields.length > 0 ? {
              object_fields: q.object_fields.map((f, fIdx) => ({
                field_key: f.field_key,
                field_label: f.field_label,
                field_type: f.field_type,
                placeholder: f.placeholder || '',
                is_required: f.is_required || false,
                field_order: fIdx + 1,
                ...(f.field_type === 'SELECT' && f.select_options ? { select_options: f.select_options } : {})
              }))
            } : {})
          }))
        }))
      };

      console.log('Payload de publicação:', payload);

      const result = await this.incidentService.publishQuestions(payload);
      this.currentVersion = (result as any).version || this.currentVersion + 1;

      this.store.dispatch(setWarningText('Perguntas publicadas com sucesso! Nova versão: v' + this.currentVersion, 200));

      // Recarregar para obter IDs atualizados
      await this.loadQuestions();
    } catch (error) {
      console.error('Erro ao publicar perguntas:', error);
      this.store.dispatch(setWarningText('Erro ao publicar perguntas. Tente novamente.', 500));
    } finally {
      this.publishLoading = false;
    }
  }

  canDeactivate(): Observable<boolean> | boolean {
    if (!this.isDirty && !this.editingQuestionId) return true;
    this.showUnsavedChangesModal = true;
    this.cdr.detectChanges();
    this.deactivateSubject = new Subject<boolean>();
    return this.deactivateSubject.asObservable();
  }

  confirmLeave(): void {
    this.showUnsavedChangesModal = false;
    this.deactivateSubject?.next(true);
    this.deactivateSubject?.complete();
    this.deactivateSubject = null;
  }

  cancelLeave(): void {
    this.showUnsavedChangesModal = false;
    this.deactivateSubject?.next(false);
    this.deactivateSubject?.complete();
    this.deactivateSubject = null;
  }

  async publishAndLeave(): Promise<void> {
    await this.publish();
    this.showUnsavedChangesModal = false;
    this.deactivateSubject?.next(true);
    this.deactivateSubject?.complete();
    this.deactivateSubject = null;
  }

  goBack(): void {
    this.router.navigate(['/main/v2/treatment-plans']);
  }
}
